/**
 * Tells whether this hub streams through the local signalling override (`REACT_APP_MYTA_LOCAL_SIGNALLING_URL`).
 *
 * @returns `true` when the override is set; it is never set on a deployed stand.
 */
export const isLocalSignalling = (): boolean =>
  !!process.env.REACT_APP_MYTA_LOCAL_SIGNALLING_URL;

/**
 * Tells whether this tab's visits must stay out of engaged time and billing.
 *
 * @param isDebugMode - Whether the Developers Terminal debug mode is on.
 * @returns `true` in debug mode or on the local signalling override, on any provider since JOIN_TO_EVENT
 *   goes out before the provider is known.
 */
export const isUsageExcluded = (isDebugMode: boolean): boolean =>
  isDebugMode || isLocalSignalling();

/**
 * Builds the payload every hub-emitted analytics row carries.
 *
 * Kept as a pure module so the debug stamp is testable without rendering the provider, which is
 * where the rule that matters lives: `isDebug` is applied **after** the caller's payload, so a
 * call site cannot shadow it. The backend drops a flagged row from the stats aggregation while
 * still storing it raw, so a lost flag silently counts a local UE editor as real usage. A tab on the
 * local signalling override also carries `localSignalling`, telling it apart from debug mode later.
 *
 * @param props.visitId - Current visit, null between experiences.
 * @param props.deviceType - Hub device profile, feeding the device and geo counters.
 * @param props.isDebugMode - Whether this tab's usage is excluded (debug mode or a local UE), see `isUsageExcluded`.
 * @param props.payload - Caller-supplied fields; may override `deviceType` and `visitId`.
 * @returns The payload to send.
 */
export const buildAnalyticsPayload = ({
  visitId,
  deviceType,
  isDebugMode,
  payload,
}: {
  visitId: string | null;
  deviceType: string;
  isDebugMode: boolean;
  payload: Record<string, unknown>;
}): Record<string, unknown> => ({
  deviceType,
  visitId,
  ...payload,
  ...(isLocalSignalling() ? { localSignalling: true } : {}),
  ...(isDebugMode ? { isDebug: true } : {}),
});
